import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { EditIcon } from "./CustomIcons";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { gePatientVisitByVisitIdApi, deleteConsultationByIdApi } from "../services/consultation.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";
import { formatDoctorDisplayName } from "../utils/doctorNameFormatter";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/permissionHelper";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";

const tabs = [
  {
    label: "Consultation",
    value: "consultationDetails",
    id: "consultation-tab",
  },
  {
    label: "Inhouse Investigation",
    value: "inhouseInvestigation",
    id: "inhouse-investigation-tab",
  },
  {
    label: "Surgery/Procedure",
    value: "surgeryOrProcedure",
    id: "surgery-procedure-tab",
  },
  // { label: "Other Services", value: "otherServices",id:"other-services-tab" },
  { label: "Pharmacy", value: "pharmacy", id: "pharmacy-tab" },
  { label: "Referral", value: "referral", id: "referral-tab" },
  { label: "Bills", value: "bill", id: "bills-tab" },
  {
    label: "Patient History",
    value: "patientHistory",
    id: "patient-history-tab",
  },
];

function PatientVisits({ onSelectVisit }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVisitForDelete, setSelectedVisitForDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const { patientId } = useParams();

  const {
    dataList: allData,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: gePatientVisitByVisitIdApi,
    params: { limit: Number.MAX_SAFE_INTEGER, patientId: patientId },
  });
  const visitDetails = Array.isArray(allData) ? allData : allData?.visits;

  // Notify parent of selected visit
  React.useEffect(() => {
    if (visitDetails && visitDetails.length > 0 && onSelectVisit) {
      // Always use the selected visit instead of finding "open" visit
      if (visitDetails[selectedIndex]) {
        onSelectVisit(visitDetails[selectedIndex]);
      }
    }
  }, [selectedIndex, visitDetails, onSelectVisit]);

  const filteredTabs = tabs?.filter((tab) => {
    const selectedVisit = visitDetails?.[selectedIndex];
    
    // Special handling for referral tab
    if (tab?.value === "referral") {
      const referralData = selectedVisit?.consultationDetails?.referral;
      return referralData && (
        referralData.doctorName || 
        referralData.contactNumber || 
        referralData.reason || 
        referralData.date
      );
    }
    
    const data = selectedVisit?.[tab?.value];

    return (
      (Array.isArray(data) && data?.length > 0) ||
      (typeof data === "object" && data !== null)
    );
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // Map each tab to its corresponding section ID
    const sectionIds = filteredTabs?.map((tab) => tab?.id);

    const sectionId = sectionIds[newValue];
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleBoxClick = (index) => {
    setSelectedIndex(index); // Only update the selected index
  };

  const handleAddItemClick = () => {
    navigate("/nursing");
  };

  const handleDeleteClick = (visit) => {
    setSelectedVisitForDelete(visit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedVisitForDelete) return;
    
    setIsDeleting(true);
    try {
      // Get the correct consultation ID from the visit structure
      const consultationId = selectedVisitForDelete.consultationDetails?.consultationId;
      
      if (!consultationId) {
        toast.error("Unable to find consultation ID for deletion");
        setDeleteDialogOpen(false);
        setSelectedVisitForDelete(null);
        setDependencies(null);
        setIsDeleting(false);
        return;
      }

      const response = await deleteConsultationByIdApi(consultationId);
      if (response.code === 200) {
        toast.success("Visit deleted successfully!");
        refetch(); // Refresh the visits list
        setDeleteDialogOpen(false);
        setSelectedVisitForDelete(null);
        setDependencies(null);
        // Reset selected index if the deleted visit was selected
        if (selectedIndex >= visitDetails.length - 1) {
          setSelectedIndex(Math.max(0, visitDetails.length - 2));
        }
      } else if (response?.code === 400 && response?.data) {
        // Handle dependency error
        setDependencies(response.data);
      } else {
        toast.error(response.message || "Failed to delete visit");
        setDeleteDialogOpen(false);
        setSelectedVisitForDelete(null);
        setDependencies(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the visit");
      setDeleteDialogOpen(false);
      setSelectedVisitForDelete(null);
      setDependencies(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedVisitForDelete(null);
    setDependencies(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Visits */}

      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorMessage />
      ) : (
        <Box
          sx={{
            padding: 2,
            marginTop: 2,
            borderRadius: 2,
            backgroundColor: "#f5faff",
          }}
        >
          <Grid container spacing={2}>
            {/* Visits Section */}
            <Grid item xs={4}>
              <Typography variant="h6" sx={{ mb: 2, color: "#556080" }}>
                Visits
              </Typography>
              <Card
                variant="outlined"
                sx={{ maxHeight: "398px", overflow: "auto" }}
              >
                <CardContent>
                  {visitDetails?.map((option, index) => (
                    <Box
                      key={option?._id}
                      sx={{
                        p: 2,
                        border: "1px solid #d5d9e3",
                        borderRadius: 1,
                        mb: 2,
                        bgcolor: selectedIndex === index ? "#e8effb" : "white",
                        cursor: "pointer",
                      }}
                      onClick={() => handleBoxClick(index)}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {option?.consultationDetails?.unit?.unitName || "-"} |{" "}
                        <Typography
                          component="span"
                          color={
                            option?.consultationDetails?.status ===
                              "scheduled" ||
                            option?.consultationDetails?.status ===
                              "re-scheduled" ||
                            option?.consultationDetails?.status === "admitted"
                              ? "yellowgreen"
                              : option?.consultationDetails?.status === "pendingPayment"
                              ? "orange"
                              : "red"
                          }
                        >
                          {option?.consultationDetails?.status ===
                            "scheduled" ||
                          option?.consultationDetails?.status === "admitted" ||
                          option?.consultationDetails?.status === "re-scheduled"
                            ? "Open"
                            : option?.consultationDetails?.status === "pendingPayment"
                            ? "Pending Payment"
                            : "Close"}
                        </Typography>{" "}
                        | {option?.consultationType || "-"}
                      </Typography>
                      <Typography variant="body2">
                        {formatDoctorDisplayName(option?.consultationDetails?.doctor)}
                      </Typography>
                      <Typography variant="body2">
                        {option?.consultationDetails?.consultationDateTime
                          ? dayjs(
                              option?.consultationDetails?.consultationDateTime
                            ).format("DD/MM/YYYY")
                          : ""}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Visit Details Section */}
            <Grid item xs={8} id="consultation-tab">
              <Typography variant="h6" sx={{ mb: 2, color: "#556080" }}>
                Visit Details
              </Typography>

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                variant="scrollable"
                textColor="primary"
                sx={{
                  "& .MuiTab-root": {
                    minWidth: "auto",
                    padding: "0 6px",
                    fontSize: "14px",
                    color: "#556080",
                    textTransform: "none",
                  },
                  "& .Mui-selected": {
                    fontWeight: "bold",
                    color: "#1a73e8",
                  },
                  mb: 2,
                  mt: -1,
                }}
              >
                {filteredTabs?.map((tab, index) => (
                  <Tab key={index} label={tab?.label} />
                ))}
              </Tabs>

              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton>
                      <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                    </IconButton>
                    {hasPermission(user, "Consultations.Delete") && (
                      <IconButton 
                        onClick={() => handleDeleteClick(visitDetails?.[selectedIndex])}
                        color="error"
                      >
                        <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                      </IconButton>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Consultation Type
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.consultationType || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Department
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.unit?.unitName || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Doctor's Name
                      </Typography>
                      <Typography variant="body1">
                        {formatDoctorDisplayName(visitDetails?.[selectedIndex]?.consultationDetails?.doctor)}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Visit Type
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.visitType?.name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Visit Status
                      </Typography>
                      <Typography
                        variant="body1"
                        color={
                          visitDetails?.[selectedIndex]?.consultationDetails
                            ?.status === "scheduled" ||
                          visitDetails?.[selectedIndex]?.consultationDetails
                            ?.status === "re-scheduled" ||
                          visitDetails?.[selectedIndex]?.consultationDetails
                            ?.status === "admitted"
                            ? "yellowgreen"
                            : visitDetails?.[selectedIndex]?.consultationDetails
                              ?.status === "pendingPayment"
                            ? "orange"
                            : "red"
                        }
                      >
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.status === "scheduled" ||
                        visitDetails?.[selectedIndex]?.consultationDetails
                          ?.status === "re-scheduled" ||
                        visitDetails?.[selectedIndex]?.consultationDetails
                          ?.status === "admitted"
                          ? "Open"
                          : visitDetails?.[selectedIndex]?.consultationDetails
                            ?.status === "pendingPayment"
                          ? "Pending Payment"
                          : "Close"}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">
                      Payment Status
                    </Typography>
                    <Typography variant="body1">
                      {appointmentData.paymentStatus}
                    </Typography>
                  </Grid> */}

                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Bill No.
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.bill?.invoiceNumber ||
                          "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Consultation Date
                      </Typography>
                      <Typography variant="body1" noWrap>
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.consultationDateTime
                          ? dayjs(
                              visitDetails?.[selectedIndex]?.consultationDetails
                                ?.consultationDateTime
                            ).format("DD/MM/YYYY, hh:mm A")
                          : ""}
                      </Typography>
                    </Grid>

                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Created By
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1">
                          {visitDetails?.[selectedIndex]?.consultationDetails
                            ?.createdBy?.firstName || "-"}{" "}
                          {visitDetails?.[selectedIndex]?.consultationDetails
                            ?.createdBy?.lastName || "-"}{" "}
                          |
                        </Typography>
                        <Typography variant="body2">
                          &nbsp;
                          {visitDetails?.[selectedIndex]?.consultationDetails
                            ?.createdAt
                            ? new Date(
                                visitDetails?.[
                                  selectedIndex
                                ]?.consultationDetails?.createdAt
                              )
                                .toISOString()
                                .substring(0, 10)
                            : ""}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Ward Name
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.bedOccupancies?.[0]
                          ?.ward?.name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Bed Number
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.bedOccupancies?.[0]?.bed
                          ?.bedNumber || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Referral Doctor
                      </Typography>
                      <Typography variant="body1">
                        {(() => {
                          const referralDoctor = visitDetails?.[selectedIndex]?.consultationDetails?.referral?.doctorName;
                          if (!referralDoctor) return "-";
                          
                          // Format with Dr. prefix if not already present
                          const doctorName = referralDoctor.trim();
                          return doctorName.startsWith('Dr.') ? doctorName : `Dr. ${doctorName}`;
                        })()}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Contact No.
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.referral?.contactNumber || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Reason For Referral
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        wordBreak: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.referral?.reason || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="textSecondary">
                        Date Of Referral
                      </Typography>
                      <Typography variant="body1">
                        {visitDetails?.[selectedIndex]?.consultationDetails
                          ?.referral?.date
                          ? dayjs(visitDetails?.[selectedIndex]?.consultationDetails?.referral?.date).format("DD/MM/YYYY")
                          : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Table Sections */}
            {visitDetails?.[selectedIndex]?.inhouseInvestigations?.length >
              0 && (
              <Grid item xs={12} id="inhouse-investigation-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Inhouse Investigation
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Table Section */}
                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Item Name
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Status
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visitDetails?.[
                            selectedIndex
                          ]?.inhouseInvestigations?.map((row, index) => (
                            <TableRow key={row?._id}>
                              <TableCell>{row?.type || "-"}</TableCell>
                              <TableCell>
                                {row?.service?.itemType || "-"}
                              </TableCell>
                              <TableCell>{row?.amount || "-"}</TableCell>
                              <TableCell>{row?.status || "-"}</TableCell>
                              {/* <TableCell>
                                <IconButton>
                                  <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                              </TableCell> */}
                            </TableRow>
                          ))}
                          {/* Add Item Button Row */}
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {visitDetails?.[selectedIndex]?.surgeryOrProcedure?.length > 0 && (
              <Grid item xs={12} id="surgery-procedure-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Surgery/Procedure
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Table Section */}
                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Item Name
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Date
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visitDetails?.[
                            selectedIndex
                          ]?.surgeryOrProcedure?.map((row, index) => (
                            <TableRow key={row?._id}>
                              <TableCell>
                                {row?.item?.itemType || "-"}
                              </TableCell>
                              <TableCell>
                                {row?.item?.itemName || "-"}
                              </TableCell>
                              <TableCell>{row?.amount || "-"}</TableCell>
                              <TableCell>
                                {row?.date.slice(0, 10) || "-"}
                              </TableCell>
                              {/* <TableCell>
                                <IconButton>
                                  <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                              </TableCell> */}
                            </TableRow>
                          ))}
                          {/* Add Item Button Row */}
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {visitDetails?.[selectedIndex]?.pharmacy?.length > 0 && (
              <Grid item xs={12} id="pharmacy-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Pharmacy
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Table Section */}
                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Item Code
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Item Name
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Quantity
                            </TableCell>

                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visitDetails?.[selectedIndex]?.pharmacy?.flatMap(
                            (item) =>
                              item?.medicines?.map((row) => (
                                <TableRow key={row?._id || Math.random()}>
                                  <TableCell>
                                    {row?.medicine?.itemCode || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {row?.medicine?.itemName || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {row?.issuedQuantity -
                                      row?.returnQuantity || "0"}
                                  </TableCell>
                                  {/* <TableCell>
                                    <IconButton>
                                      <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                    </IconButton>
                                    <IconButton>
                                      <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                                    </IconButton>
                                    <IconButton>
                                      <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                    </IconButton>
                                  </TableCell> */}
                                </TableRow>
                              ))
                          )}
                          {/* Add Item Button Row */}
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {/* {visitDetails?.[selectedIndex]?.otherServices?.length > 0 && (
              <Grid item xs={12} id="other-services-tab">
                <Card variant="outlined">
                  <CardContent>
               
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Other Services
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Item Name
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Quantity
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visitDetails?.[selectedIndex]?.otherServices?.map(
                            (row, index) => (
                              <TableRow key={row?._id}>
                                <TableCell>{row?.category || "-"}</TableCell>
                                <TableCell>
                                  {row?.item?.itemName || "-"}
                                </TableCell>
                                <TableCell>{row?.amount || "-"}</TableCell>
                                <TableCell>{row?.quantity || "-"}</TableCell>
                                <TableCell>
                                  <IconButton>
                                    <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                  </IconButton>
                                  <IconButton>
                                    <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                                  </IconButton>
                                  <IconButton>
                                    <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                       
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )} */}
            {visitDetails?.[selectedIndex]?.bill?.invoiceId && (
              <Grid item xs={12} id="bills-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Bills
                      </Typography>
                      {/* <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton> */}
                    </Box>

                    {/* Table Section */}
                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Invoice Number
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Invoice Date
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Quantity
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Total Amount
                            </TableCell>

                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visitDetails?.[
                            selectedIndex
                          ]?.bill?.invoiceItems?.map((row, index) => (
                            <TableRow key={row?._id}>
                              <TableCell>
                                {visitDetails?.[selectedIndex]?.bill
                                  ?.invoiceNumber || "-"}
                              </TableCell>
                              <TableCell>{row?.category || "-"}</TableCell>
                              <TableCell>
                                {visitDetails?.[
                                  selectedIndex
                                ]?.bill?.invoiceDate
                                  ? dayjs(visitDetails?.[selectedIndex]?.bill?.invoiceDate).format("DD/MM/YYYY")
                                  : "-"}
                              </TableCell>
                              <TableCell>{row?.quantity || "-"}</TableCell>
                              <TableCell>{row?.finalAmount}</TableCell>
                              {/* <TableCell>
                                <IconButton>
                                  <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                                </IconButton>
                                <IconButton>
                                  <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                                </IconButton>
                              </TableCell> */}
                            </TableRow>
                          ))}
                          {/* Add Item Button Row */}
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {visitDetails?.[selectedIndex]?.patientHistory && (
              <Grid item xs={12} id="patient-history-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Patient History
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Table Section */}
                    <TableContainer
                      sx={{ border: "1px solid #d5d9e3", borderRadius: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: "#EAEAEA" }}>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              History Type
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Add note
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#556080" }}
                            >
                              Attached
                            </TableCell>

                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              {visitDetails?.[selectedIndex]?.patientHistory
                                ?.date
                                ? new Date(
                                    visitDetails?.[selectedIndex]?.patientHistory?.date
                                  ).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {visitDetails?.[selectedIndex]?.patientHistory
                                ?.note || "-"}
                            </TableCell>
                            <TableCell>
                              {visitDetails?.[selectedIndex]?.patientHistory
                                ?.fileUrl ? (
                                <a
                                  href={
                                    visitDetails?.[selectedIndex]
                                      ?.patientHistory?.fileUrl
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#1976d2" }}
                                >
                                  View File
                                </a>
                              ) : (
                                "-"
                              )}
                            </TableCell>

                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const fileUrl =
                                    visitDetails?.[selectedIndex]
                                      ?.patientHistory?.fileUrl;
                                  if (fileUrl) {
                                    window.open(fileUrl, "_blank");
                                  } else {
                                    toast.info("No attachment available");
                                  }
                                }}
                              >
                                <VisibilityOutlinedIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                              </IconButton>
                              <IconButton size="small">
                                <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                              </IconButton>
                              <IconButton size="small">
                                <DeleteOutlineIcon sx={{ color: "#5D6A82", fontSize: "18px" }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {/* Add Item Button Row */}
                          <TableRow>
                            <TableCell colSpan={5} align="left">
                              <Button
                                variant="text"
                                sx={{ color: "#556080", textTransform: "none" }}
                                onClick={handleAddItemClick}
                              >
                                + Add Item
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {visitDetails?.[selectedIndex]?.consultationDetails?.referral && (
              <Grid item xs={12} id="referral-tab">
                <Card variant="outlined">
                  <CardContent>
                    {/* Header Section */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ color: "#556080" }}>
                        Referral Details
                      </Typography>
                      <IconButton>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Referral Information */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Referral Doctor Name
                        </Typography>
                        <Typography variant="body1">
                          {(() => {
                            const referralDoctor = visitDetails?.[selectedIndex]?.consultationDetails?.referral?.doctorName;
                            if (!referralDoctor) return "-";
                            
                            // Format with Dr. prefix if not already present
                            const doctorName = referralDoctor.trim();
                            return doctorName.startsWith('Dr.') ? doctorName : `Dr. ${doctorName}`;
                          })()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Contact Number
                        </Typography>
                        <Typography variant="body1">
                          {visitDetails?.[selectedIndex]?.consultationDetails?.referral?.contactNumber || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Date of Referral
                        </Typography>
                        <Typography variant="body1">
                          {visitDetails?.[selectedIndex]?.consultationDetails?.referral?.date
                            ? dayjs(visitDetails?.[selectedIndex]?.consultationDetails?.referral?.date).format("DD/MM/YYYY")
                            : "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Referral Status
                        </Typography>
                        <Typography variant="body1" color="success.main">
                          {visitDetails?.[selectedIndex]?.consultationDetails?.referral ? "Active" : "No Referral"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Reason for Referral
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          wordBreak: 'break-word',
                          padding: 1,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          {visitDetails?.[selectedIndex]?.consultationDetails?.referral?.reason || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
      
      {/* Delete Confirmation Dialog */}
                <DeleteConfirmationDialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Visit"
            itemName={selectedVisitForDelete ? 
              `${selectedVisitForDelete.consultationType} visit with ${formatDoctorDisplayName(selectedVisitForDelete.consultationDetails?.doctor)}` : 
              "this visit"}
            dependencies={dependencies}
            isLoading={isDeleting}
          />
    </Box>
  );
}

export default PatientVisits;
